import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronDown, Star, ArrowRight, X } from "lucide-react";
import api, { formatINR } from "../lib/api";
import { PackageCard } from "../components/PackageCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";

export default function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState("all");
  const [sortBy, setSortBy] = useState("popular");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "All Decoration Packages — YoursDreamDecoration";
    Promise.all([
      api.get("/packages").then(r => r.data),
      api.get("/categories").then(r => r.data)
    ]).then(([pkgs, cats]) => {
      setPackages(pkgs);
      setFiltered(pkgs);
      setCategories(cats);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    let result = [...packages];

    // Category Filter
    if (selectedCat !== "all") {
      result = result.filter(p => p.category_slug === selectedCat);
    }

    // Search Filter
    if (search) {
      result = result.filter(p => 
        p.title.toLowerCase().includes(search.toLowerCase()) || 
        p.description.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Sorting
    if (sortBy === "price-low") {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === "price-high") {
      result.sort((a, b) => b.price - a.price);
    } else if (sortBy === "newest") {
      result.sort((a, b) => b.id - a.id);
    }

    setFiltered(result);
  }, [search, selectedCat, sortBy, packages]);

  const clearFilters = () => {
    setSelectedCat("all");
    setSearch("");
    setSortBy("popular");
  };

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="packages-page">
      {/* HERO / SEARCH SECTION */}
      <div className="relative bg-[#882B7B] py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-400 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <h1 className="font-serif-display text-4xl md:text-6xl text-white tracking-tight">
            Discover the <span className="italic opacity-80">Magic</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Browse our curated collection of stunning decorations for every occasion.
          </p>
          
          <div className="relative max-w-xl mx-auto group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-primary transition-colors" />
            <input 
              type="text" 
              placeholder="Search for 'Birthday', 'Anniversary'..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-14 pl-12 pr-4 rounded-2xl border-none shadow-2xl focus:ring-4 focus:ring-primary/20 text-slate-900 placeholder:text-slate-400 transition-all outline-none"
            />
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16 gap-4">
                {/* Horizontal Categories */}
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-2 flex-1">
                    <button 
                        onClick={() => setSelectedCat("all")}
                        className={`shrink-0 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                            selectedCat === "all" ? "bg-primary text-white shadow-lg shadow-primary/20" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                    >
                        All
                    </button>
                    {categories.map(c => (
                        <button 
                            key={c.slug}
                            onClick={() => setSelectedCat(c.slug)}
                            className={`shrink-0 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                                selectedCat === c.slug ? "bg-primary text-white shadow-lg shadow-primary/20" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2 border-l border-slate-100 pl-4">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-10 gap-2 text-slate-600 font-bold uppercase text-[10px] tracking-widest hover:bg-slate-50">
                                Sort By <ChevronDown className="w-3 h-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-2xl border-slate-100 shadow-xl">
                            <DropdownMenuItem onClick={() => setSortBy("popular")} className="text-xs font-medium py-3 cursor-pointer">Popularity</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setSortBy("price-low")} className="text-xs font-medium py-3 cursor-pointer">Price: Low to High</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setSortBy("price-high")} className="text-xs font-medium py-3 cursor-pointer">Price: High to Low</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setSortBy("newest")} className="text-xs font-medium py-3 cursor-pointer">Newest First</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
                <h2 className="text-2xl font-serif-display text-slate-900">
                    {selectedCat === "all" ? "All Collections" : categories.find(c => c.slug === selectedCat)?.name}
                </h2>
                <Badge variant="secondary" className="bg-slate-100 text-slate-500 font-normal">
                    {filtered.length} Packages
                </Badge>
            </div>
            
            {(selectedCat !== "all" || search || sortBy !== "popular") && (
                <button 
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-rose-500 hover:text-rose-600 transition-colors"
                >
                    <X className="w-3 h-3" /> Clear Filters
                </button>
            )}
        </div>

        {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {[...Array(8)].map((_, i) => (
                    <div key={i} className="space-y-4">
                        <Skeleton className="aspect-[4/5] w-full rounded-3xl" />
                        <div className="space-y-2 px-1">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-4 w-full" />
                            <div className="flex justify-between items-center pt-2">
                                <Skeleton className="h-6 w-20" />
                                <Skeleton className="h-8 w-8 rounded-full" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {filtered.map(p => (
                    <PackageCard key={p.slug} pkg={p} />
                ))}
            </div>
        ) : (
            <div className="py-20 text-center space-y-4">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-300">
                    <Search className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-serif-display">No packages found</h3>
                <p className="text-slate-500 max-w-xs mx-auto">We couldn't find anything matching your filters. Try adjusting your search or category.</p>
                <Button onClick={clearFilters} variant="outline" className="rounded-xl border-2">Reset All Filters</Button>
            </div>
        )}
      </div>

      {/* BOTTOM BANNER */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
          <div className="bg-[#1A1A1A] rounded-[2.5rem] p-10 md:p-16 text-white relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-20 pointer-events-none">
                <img 
                    src="https://images.unsplash.com/photo-1741969494307-55394e3e4071?auto=format&fit=crop&q=60&w=800" 
                    alt="Custom Decoration Setup Preview" 
                    className="w-full h-full object-cover [transition-duration:2000ms] group-hover:scale-110"
                />
            </div>
            <div className="relative z-10 max-w-xl space-y-6">
                <Badge className="bg-primary hover:bg-primary text-white border-none px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest">
                    Custom Setup
                </Badge>
                <h2 className="text-4xl md:text-5xl font-serif-display leading-[1.1]">Don't see what you're looking for?</h2>
                <p className="text-white/60 text-lg">Our designers can create a completely unique theme just for you. Send us your Pinterest board!</p>
                <div className="flex pt-4">
                    <Button size="lg" className="h-14 px-8 rounded-2xl bg-white text-slate-900 hover:bg-slate-100 font-bold gap-3 shadow-xl">
                        Design My Theme <ArrowRight className="w-5 h-5" />
                    </Button>
                </div>
            </div>
          </div>
      </div>
    </div>
  );
}
